/**
 * ScriptHost — the single, backend-agnostic manager of live script executors.
 *
 * It replaces the duplicated per-instance loops in the legacy `RedprintSubsystem` and
 * `JSScriptSubsystem`: one place creates executors via the {@link BackendRegistry},
 * binds each to a capability-scoped {@link IEngineContext}, applies inspector variable
 * overrides, and drives the unified lifecycle in deterministic priority order. Physics and
 * any host event reach every backend symmetrically through {@link ScriptHost.triggerEvent}.
 *
 * Pure core (no Subsystem / asset / Level dependency) so it is fully unit-testable and
 * reusable by both the player and editor `ScriptSubsystem`s, which feed it from the
 * manifest / scene.
 */

import type { EntityHandle, IEngineContext } from "./IEngineContext";
import type { IScriptExecutor, ScriptType } from "./IScriptExecutor";
import type { BackendRegistry } from "./BackendRegistry";

/** Builds the (capability-scoped) engine context for an entity. */
export type EngineContextFactory = (entity: EntityHandle) => IEngineContext;

/** One script attachment to instantiate (mirrors a `core.Script` component). */
export interface ScriptAttachment {
  /** Stable key (GameObject GUID). */
  key: string;
  entity: EntityHandle;
  scriptType: ScriptType;
  assetId: string;
  enabled?: boolean;
  variables?: Record<string, unknown>;
  /** Lower runs earlier. */
  priority?: number;
}

interface HostedScript {
  executor: IScriptExecutor;
  entity: EntityHandle;
  key: string;
  priority: number;
  started: boolean;
  /** The attachment it was created from (for hot reload). */
  attachment: ScriptAttachment;
  /** The context the executor was bound to — disposed (scoped resources) on teardown. */
  ctx: IEngineContext;
}

export class ScriptHost {
  private readonly _instances = new Map<string, HostedScript>();
  private _ordered: HostedScript[] = [];
  private _orderDirty = false;
  private _enabled = true;

  constructor(
    private readonly _registry: BackendRegistry,
    private readonly _contextFactory: EngineContextFactory,
  ) {}

  get size(): number {
    return this._instances.size;
  }

  /**
   * Instantiate and awake a script for an entity. No-op if disabled or if no backend is
   * registered for its scriptType.
   */
  add(attachment: ScriptAttachment): IScriptExecutor | null {
    if (attachment.enabled === false) return null;
    if (this._instances.has(attachment.key)) this.remove(attachment.key);

    if (!this._registry.has(attachment.scriptType)) {
      console.warn(
        `[ScriptHost] no backend for scriptType '${attachment.scriptType}' (key=${attachment.key})`,
      );
      return null;
    }

    const executor = this._registry.create(attachment.scriptType, attachment.assetId);
    const ctx = this._contextFactory(attachment.entity);
    executor.initialize(attachment.entity, ctx);

    // Inspector variable overrides land before awake (graph backend re-applies post-reset).
    if (attachment.variables) {
      for (const [name, value] of Object.entries(attachment.variables)) {
        executor.setVariable(name, value);
      }
    }

    executor.onAwake?.();
    executor.onEnable?.();

    this._instances.set(attachment.key, {
      executor,
      entity: attachment.entity,
      key: attachment.key,
      priority: attachment.priority ?? 0,
      started: false,
      attachment,
      ctx,
    });
    this._orderDirty = true;
    return executor;
  }

  /**
   * Hot-reload a script in place, preserving its live state by `@field`/variable NAME
   * (snapshot → teardown → recreate from the backend → re-apply by name). The caller is
   * responsible for invalidating any backend source cache first (e.g.
   * `TypeScriptBackend.invalidate(assetId)`), so `create()` picks up the new code.
   *
   * Name-keyed (never ordinal) so reordering/adding fields doesn't corrupt state; a field
   * present in both keeps its value, a new field stays at its declared default, a removed
   * field is dropped. Non-snapshotted (derived) state is intentionally not preserved.
   */
  reload(key: string): IScriptExecutor | null {
    const hosted = this._instances.get(key);
    if (!hosted) return null;
    const snapshot = this.snapshotOf(hosted);
    const attachment = hosted.attachment;
    this.remove(key);
    return this.add({ ...attachment, variables: { ...attachment.variables, ...snapshot } });
  }

  /** Snapshot a hosted script's variable state by name (for reload / save). */
  private snapshotOf(hosted: HostedScript): Record<string, unknown> {
    try {
      return hosted.executor.getVariableSnapshot();
    } catch (err) {
      console.error("[ScriptHost] snapshot failed:", err);
      return {};
    }
  }

  /** Snapshot one live script's variables by key (e.g. before a manual reload). */
  snapshot(key: string): Record<string, unknown> | null {
    const hosted = this._instances.get(key);
    return hosted ? this.snapshotOf(hosted) : null;
  }

  /**
   * Hot-reload every hosted script created from `assetId` — the entry point for live
   * editing. The caller must update the backend so a fresh `create(assetId)` yields the new
   * code (e.g. recompile + cache the new Behaviour ctor) BEFORE calling this. Returns how
   * many instances were reloaded.
   */
  reloadByAsset(assetId: string): number {
    let count = 0;
    for (const key of [...this._instances.keys()]) {
      if (this._instances.get(key)?.attachment.assetId === assetId) {
        this.reload(key);
        count++;
      }
    }
    return count;
  }

  /** Tear down and remove a script (fires onDisable/onDestroy, then frees its scoped resources). */
  remove(key: string): void {
    const hosted = this._instances.get(key);
    if (!hosted) return;
    this.safe(() => hosted.executor.onDisable?.());
    this.safe(() => hosted.executor.onDestroy?.());
    this.safe(() => hosted.ctx.dispose?.());
    this._instances.delete(key);
    this._orderDirty = true;
  }

  /** Per-frame variable update. `onStart` fires once, on the first update (spawn-safe). */
  update(dt: number): void {
    if (!this._enabled) return;
    for (const hosted of this.ordered()) {
      if (!hosted.started) {
        hosted.started = true;
        this.safe(() => hosted.executor.onStart?.());
      }
      this.safe(() => hosted.executor.onUpdate?.(dt));
    }
  }

  fixedUpdate(dt: number): void {
    if (!this._enabled) return;
    for (const hosted of this.ordered()) this.safe(() => hosted.executor.onFixedUpdate?.(dt));
  }

  lateUpdate(dt: number): void {
    if (!this._enabled) return;
    for (const hosted of this.ordered()) this.safe(() => hosted.executor.onLateUpdate?.(dt));
  }

  /** Deliver an external event (e.g. a physics collision) to one script. */
  triggerEvent(key: string, eventId: string, params?: Record<string, unknown>): void {
    this.safe(() => this._instances.get(key)?.executor.triggerEvent(eventId, params));
  }

  getExecutor(key: string): IScriptExecutor | undefined {
    return this._instances.get(key)?.executor;
  }

  has(key: string): boolean {
    return this._instances.has(key);
  }

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
  }

  /** Tear down every script. */
  destroyAll(): void {
    for (const hosted of this._instances.values()) {
      this.safe(() => hosted.executor.onDisable?.());
      this.safe(() => hosted.executor.onDestroy?.());
      this.safe(() => hosted.ctx.dispose?.());
    }
    this._instances.clear();
    this._ordered = [];
    this._orderDirty = false;
  }

  private ordered(): HostedScript[] {
    if (this._orderDirty) {
      this._ordered = [...this._instances.values()].sort((a, b) => a.priority - b.priority);
      this._orderDirty = false;
    }
    return this._ordered;
  }

  /** One script throwing must not stop the others (matches the legacy try/catch loops). */
  private safe(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      console.error("[ScriptHost] script error:", err);
    }
  }
}
